import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useAuth } from "@/shared/auth/AuthContext";
import { createBrowserTokenStore } from "@/shared/auth/tokenStore";
import { useToast } from "@/shared/ui";
import {
  createNotificationHubConnection,
  NOTIFICATION_RECEIVED,
  parseRealtimeNotification,
  startNotificationHub,
  stopNotificationHub,
} from "@/modules/sys/realtime/notificationHub";
import { setRealtimeNotifyConnected } from "@/modules/sys/realtime/realtimeNotifyStatus";

function toneForSeverity(severity: string): "info" | "warning" | "error" {
  const s = severity.toLowerCase();
  if (s === "warning" || s === "medium") return "warning";
  if (s === "error" || s === "high" || s === "critical" || s === "danger")
    return "error";
  return "info";
}

/**
 * Keep one SignalR connection while authenticated; toast + refresh inbox badge.
 * When connected, inbox poll pauses (see useInAppNotificationsQuery).
 */
export function RealtimeNotifyBridge() {
  const { status } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const tokenStore = useRef(createBrowserTokenStore()).current;

  useEffect(() => {
    if (status !== "authenticated") {
      setRealtimeNotifyConnected(false);
      return;
    }

    const connection = createNotificationHubConnection(tokenStore);
    let cancelled = false;

    connection.on(NOTIFICATION_RECEIVED, (payload: unknown) => {
      const n = parseRealtimeNotification(payload);
      if (!n) return;
      toast({
        title: n.title,
        message: n.body,
        tone: toneForSeverity(n.severity),
      });
      void queryClient.invalidateQueries({ queryKey: ["sys", "notifications"] });
    });

    connection.onreconnecting(() => setRealtimeNotifyConnected(false));
    connection.onreconnected(() => setRealtimeNotifyConnected(true));
    connection.onclose(() => setRealtimeNotifyConnected(false));

    void startNotificationHub(connection)
      .then(() => {
        if (!cancelled) setRealtimeNotifyConnected(true);
      })
      .catch((err) => {
        if (!cancelled) {
          setRealtimeNotifyConnected(false);
          console.warn("SignalR notifications hub", err);
        }
      });

    return () => {
      cancelled = true;
      setRealtimeNotifyConnected(false);
      connection.off(NOTIFICATION_RECEIVED);
      void stopNotificationHub(connection);
    };
  }, [status, tokenStore, toast, queryClient]);

  return null;
}
